import { Skeleton } from "@credit-acceptance/uikit";
import { useTranslation } from "react-i18next";

import { EmptyState, ErrorState } from "../../components/ui/centeredMessage";
import { getErrorMessage } from "../../lib/query/errors";
import { useStatusHistory } from "./api/useStatusHistory";
import { formatStatusHistoryDate } from "./formatStatusHistoryDate";

interface StatusHistoryTabProperties {
  approvalId: string;
}

function StatusHistoryTab({ approvalId }: StatusHistoryTabProperties) {
  const { t } = useTranslation();
  const { data, isLoading, isError, error } = useStatusHistory(approvalId);

  const rows = data ?? [];

  return (
    <div className="flex h-full flex-col">
      {isLoading && (
        <div role="status" aria-label={t("audit.statusHistory.loading")} className="flex-1 p-3">
          <Skeleton loading active paragraph={{ rows: 6 }} />
        </div>
      )}

      {isError && (
        <ErrorState message={t("audit.statusHistory.loadError", { message: getErrorMessage(error) })} className="p-0" />
      )}

      {!isLoading && !isError && rows.length === 0 && (
        <EmptyState message={t("audit.statusHistory.empty")} className="p-0" />
      )}

      {!isLoading && !isError && rows.length > 0 && (
        <div className="border-border rounded border">
          <table className="w-full border-collapse text-xs">
            <caption className="sr-only">{t("audit.statusHistory.caption")}</caption>
            <thead>
              <tr className="text-foreground">
                <th
                  scope="col"
                  className="bg-section-header border-border sticky top-0 border-b px-2 py-1 text-left font-semibold"
                >
                  {t("audit.statusHistory.columns.workqueue")}
                </th>
                <th
                  scope="col"
                  className="bg-section-header border-border sticky top-0 border-b px-2 py-1 text-left font-semibold"
                >
                  {t("audit.statusHistory.columns.startStatus")}
                </th>
                <th
                  scope="col"
                  className="bg-section-header border-border sticky top-0 border-b px-2 py-1 text-left font-semibold"
                >
                  {t("audit.statusHistory.columns.startPhase")}
                </th>
                <th
                  scope="col"
                  className="bg-section-header border-border sticky top-0 border-b px-2 py-1 text-left font-semibold"
                >
                  {t("audit.statusHistory.columns.user")}
                </th>
                <th
                  scope="col"
                  className="bg-section-header border-border sticky top-0 border-b px-2 py-1 text-left font-semibold"
                >
                  {t("audit.statusHistory.columns.endStatus")}
                </th>
                <th
                  scope="col"
                  className="bg-section-header border-border sticky top-0 border-b px-2 py-1 text-left font-semibold"
                >
                  {t("audit.statusHistory.columns.endPhase")}
                </th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row, index) => (
                <tr key={index} className="border-border bg-card border-t">
                  <td className="text-foreground whitespace-nowrap px-2 py-1 align-top">{row.workqueue}</td>
                  <td className="text-foreground px-2 py-1 align-top">
                    <div className="whitespace-nowrap">
                      {row.status}
                      {row.startDate && <div className="text-xs">{formatStatusHistoryDate(row.startDate)}</div>}
                    </div>
                  </td>
                  <td className="text-foreground px-2 py-1 align-top">{row.phase}</td>
                  <td className="text-foreground whitespace-nowrap px-2 py-1 align-top">{row.user}</td>
                  <td className="text-foreground px-2 py-1 align-top">
                    <div className="whitespace-nowrap">
                      {row.endStatus}
                      {row.endDate && <div className="text-xs">{formatStatusHistoryDate(row.endDate)}</div>}
                    </div>
                  </td>
                  <td className="text-foreground px-2 py-1 align-top">{row.endPhase}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default StatusHistoryTab;
